<script setup lang="ts">
import { ref, watch, computed, nextTick } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import { useCompanyRegistrationStore } from '@/stores/companyRegistrationStore'

const props = defineProps<{
  data: Record<string, any>
  isSaving: boolean
}>()

const emits = defineEmits(['update', 'back', 'submit'])

const registrationStore = useCompanyRegistrationStore()
// Password and password confirm are local values not sent to the store and only attached if the user submits
const password = ref('')
const passwordConfirm = ref('')



const localData = ref({ ...props.data })
const errors = ref<Record<string, string>>({})

const managerOptions = computed(() => {
  const source = registrationStore.form?.keyPeople ?? []
  return source
    .map((person: any, index: number) => {
      const firstName = (person?.firstName ?? '').trim()
      const lastName = (person?.lastName ?? '').trim()
      const role = (person?.role ?? '').trim()
      if (!firstName || !lastName || !role) return null
      return {
        value: String(index),
        firstName,
        lastName,
        role,
        label: `${firstName} ${lastName} (${role})`,
      }
    })
    .filter(
      (item): item is {
        value: string
        firstName: string
        lastName: string
        role: string
        label: string
      } => item !== null
    )
})

const selectedManagerSource = ref('custom')
let isApplyingSelection = false

const syncSelectionFromData = () => {
  const currentFirst = (localData.value.firstName ?? '').trim()
  const currentLast = (localData.value.lastName ?? '').trim()
  const currentRole = (localData.value.jobTitle ?? '').trim()

  const matched = managerOptions.value.find(
    (option) =>
      option.firstName === currentFirst &&
      option.lastName === currentLast &&
      option.role === currentRole
  )

  selectedManagerSource.value = matched ? matched.value : 'custom'
}

watch(
  managerOptions,
  () => {
    syncSelectionFromData()
  },
  { immediate: true }
)

watch(selectedManagerSource, async (value) => {
  if (value === 'custom') return
  const option = managerOptions.value.find((item) => item.value === value)
  if (!option) {
    selectedManagerSource.value = 'custom'
    return
  }

  isApplyingSelection = true
  localData.value.firstName = option.firstName
  localData.value.lastName = option.lastName
  localData.value.jobTitle = option.role
  await nextTick()
  isApplyingSelection = false
})

const handleManagerFieldInput = () => {
  if (isApplyingSelection) return
  if (selectedManagerSource.value !== 'custom') {
    selectedManagerSource.value = 'custom'
  }
}

watch(localData, (val) => emits('update', val), { deep: true })

const validateAndSubmit = () => {
  const newErrors: Record<string, string> = {}

  // Basic required fields
  if (!localData.value.firstName?.trim())
    newErrors.firstName = 'First name is required'
  if (!localData.value.lastName?.trim())
    newErrors.lastName = 'Last name is required'
  if (!localData.value.jobTitle?.trim())
    newErrors.jobTitle = 'Job title is required'
  if (!localData.value.phoneNumber?.trim())
    newErrors.phoneNumber = 'Phone number is required'

  // Password validation
  if (!password.value?.trim()) {
    newErrors.password = 'Password is required'
  } else if (password.value.length < 8) {
    newErrors.password = 'Password must be at least 8 characters'
  }

  // Confirm password match
  if (passwordConfirm.value !== password.value) {
    newErrors.confirmPassword = 'Passwords do not match'
  }

  errors.value = newErrors

  if (Object.keys(newErrors).length === 0) {
    emits('submit', { password: password.value, confirmPassword: passwordConfirm.value })
  }
}
</script>

<template>
  <div class="bg-card text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-sm mt-8">
    <!-- Header -->
    <div class="grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 pb-6">
      <div class="leading-none font-semibold text-lg">Manager Information</div>
      <div class="text-muted-foreground text-sm">
        We'll use this to contact your company's main representative
      </div>
    </div>

    <!-- Content -->
    <div class="px-6">
      <div
        v-if="managerOptions.length"
        class="mb-6 rounded-md border border-border bg-muted/10 p-4"
      >
        <label class="block text-sm font-medium mb-1">Primary Manager</label>
        <select
          v-model="selectedManagerSource"
          class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none text-sm"
        >
          <option value="custom">Add new manager</option>
          <option
            v-for="option in managerOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <p class="text-xs text-muted-foreground mt-2">
          Select an existing key person to prefill details or add a new company contact.
        </p>
      </div>

      <div class="grid gap-6 md:grid-cols-2">
        <!-- First Name -->
        <div>
          <label class="block text-sm font-medium mb-1">First Name</label>
          <input
            v-model="localData.firstName"
            type="text"
            placeholder="John"
            @input="handleManagerFieldInput"
            class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
            :aria-invalid="!!errors.firstName"
          />
          <p v-if="errors.firstName" class="text-sm text-red-600 mt-1">{{ errors.firstName }}</p>
        </div>

        <!-- Last Name -->
        <div>
          <label class="block text-sm font-medium mb-1">Last Name</label>
          <input
            v-model="localData.lastName"
            type="text"
            placeholder="Smith"
            @input="handleManagerFieldInput"
            class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
            :aria-invalid="!!errors.lastName"
          />
          <p v-if="errors.lastName" class="text-sm text-red-600 mt-1">{{ errors.lastName }}</p>
        </div>

        <!-- Job Title -->
        <div>
          <label class="block text-sm font-medium mb-1">Job Title</label>
          <input
            v-model="localData.jobTitle"
            type="text"
            placeholder="Operations Manager"
            @input="handleManagerFieldInput"
            class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
            :aria-invalid="!!errors.jobTitle"
          />
          <p v-if="errors.jobTitle" class="text-sm text-red-600 mt-1">{{ errors.jobTitle }}</p>
        </div>

        <!-- Phone Number -->
        <div>
          <label class="block text-sm font-medium mb-1">Phone Number</label>
          <input
            v-model="localData.phoneNumber"
            type="tel"
            placeholder="+61 400 000 000"
            class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
            :aria-invalid="!!errors.phoneNumber"
          />
          <p v-if="errors.phoneNumber" class="text-sm text-red-600 mt-1">{{ errors.phoneNumber }}</p>
        </div>

        <!-- Password and confirmation-->
        <div>
          <label class="block text-sm font-medium mb-1">Password</label>
          <input
            v-model="password"
            type="password"
            placeholder="Enter a secure password"
            class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
            :aria-invalid="!!errors.password"
          />
          <p v-if="errors.password" class="text-sm text-red-600 mt-1">{{ errors.password }}</p>
        </div>

        <!-- Confirm Password -->
        <div>
          <label class="block text-sm font-medium mb-1">Confirm Password</label>
          <input
            v-model="passwordConfirm"
            type="password"
            placeholder="Re-enter your password"
            class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
            :aria-invalid="!!errors.confirmPassword"
          />
          <p v-if="errors.confirmPassword" class="text-sm text-red-600 mt-1">{{ errors.confirmPassword }}</p>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="flex justify-between items-center px-6 pt-6">
      <button
        class="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted"
        @click="$emit('back')"
      >
        Back
      </button>
      <button
        class="px-4 py-2 rounded-md bg-brand text-white text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 transition disabled:opacity-70 disabled:cursor-not-allowed"
        @click="validateAndSubmit"
        :disabled="props.isSaving"
      >
        <template v-if="props.isSaving">
          <Loader2 class="w-4 h-4 animate-spin" />
          <span>Registering...</span>
        </template>
        <template v-else>
          <span>Complete Registration</span>
        </template>
      </button>
    </div>
  </div>
</template>
